import React, { useState, useEffect, useRef } from 'react'
import Dashboard from './Dashboard'

const VIDEO_WEBM = '/images/video1.mp4'
const VIDEO_MP4  = '/images/video1.mp4'

const LANGS = [
  { code: 'en',  label: 'English', short: 'ENG' },
  { code: 'fr',  label: 'Français', short: 'FRA' },
  { code: 'nl',  label: 'Nederlands', short: 'NLD' },
]

function Home() {
  /* ── Screen state: 'lang' | 'login' ── */
  const [screen, setScreen]           = useState('lang')
  const [lang, setLang]               = useState(null)
  const [langReady, setLangReady]     = useState(false)   // controls entrance anim on lang screen
  const [langLeaving, setLangLeaving] = useState(false)   // controls exit anim before login
  const [loginReady, setLoginReady]   = useState(false)   // controls entrance anim on login screen

  const [memberId, setMemberId]       = useState('')
  const [accessCode, setAccessCode]   = useState('')
  const [isRevealing, setIsRevealing] = useState(false)
  const [isRevealed, setIsRevealed]   = useState(false)
  const [loggedIn, setLoggedIn]       = useState(false)
  const [error, setError]             = useState('')

  const inactivityTimer = useRef(null)
  const blurVideoRef    = useRef(null)
  const clearVideoRef   = useRef(null)

  /* ── Lang screen entrance ── */
  useEffect(() => {
    const t = setTimeout(() => setLangReady(true), 120)
    return () => clearTimeout(t)
  }, [])

  /* ── Keep both videos in sync ── */
  useEffect(() => {
    const blurVid  = blurVideoRef.current
    const clearVid = clearVideoRef.current
    if (!blurVid || !clearVid) return
    const syncTime = () => {
      if (Math.abs(blurVid.currentTime - clearVid.currentTime) > 0.3) {
        clearVid.currentTime = blurVid.currentTime
      }
    }
    blurVid.addEventListener('timeupdate', syncTime)
    return () => blurVid.removeEventListener('timeupdate', syncTime)
  }, [])

  /* ── Inactivity timer ── */
  const resetInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    inactivityTimer.current = setTimeout(() => {
      setLoggedIn(false)
      setIsRevealed(false)
      setIsRevealing(false)
      setMemberId('')
      setAccessCode('')
      setError('')
      setScreen('lang')
      setLang(null)
      setLangReady(false)
      setLangLeaving(false)
      setLoginReady(false)
      setTimeout(() => setLangReady(true), 120)
    }, 15 * 60 * 1000)
  }

  useEffect(() => {
    if (!isRevealed) return
    window.addEventListener('mousemove', resetInactivityTimer)
    window.addEventListener('keydown',   resetInactivityTimer)
    window.addEventListener('click',     resetInactivityTimer)
    resetInactivityTimer()
    return () => {
      window.removeEventListener('mousemove', resetInactivityTimer)
      window.removeEventListener('keydown',   resetInactivityTimer)
      window.removeEventListener('click',     resetInactivityTimer)
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    }
  }, [isRevealed])

  /* ── Sign out ── */
  const handleSignOut = () => {
    setLoggedIn(false)
    setIsRevealed(false)
    setIsRevealing(false)
    setMemberId('')
    setAccessCode('')
    setError('')
    setScreen('lang')
    setLang(null)
    setLangReady(false)
    setLangLeaving(false)
    setLoginReady(false)
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    setTimeout(() => setLangReady(true), 120)
  }

  /* ── Language selection → transition to login ── */
  const handleSelectLang = (l) => {
    setLang(l)
    setLangLeaving(true)
    // fade out lang screen, then show login
    setTimeout(() => {
      setScreen('login')
      setLangLeaving(false)
      setTimeout(() => setLoginReady(true), 60)
    }, 680)
  }

  /* ── Login handler ── */
  const handleLogin = (e) => {
    e.preventDefault()
    if (!memberId.trim() || !accessCode.trim()) {
      setError('Please enter your Member ID and Access Code.')
      return
    }
    setError('')
    setIsRevealing(true)
    setTimeout(() => {
      setIsRevealed(true)
      setIsRevealing(false)
      setTimeout(() => setLoggedIn(true), 300)
    }, 1600)
  }

  const clearError = () => { if (error) setError('') }

  /* ── Dashboard ── */
  if (loggedIn) {
    return <Dashboard onSignOut={handleSignOut} />
  }

  const rootClass = [
    'mw-root',
    isRevealing ? 'mw-revealing' : '',
    isRevealed  ? 'mw-revealed'  : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={rootClass}>

      {/* ── Blurred video layer ── */}
      <div className="mw-video-wrap mw-video-wrap--blur">
        <video ref={blurVideoRef} className="mw-video"
          autoPlay muted loop playsInline preload="auto" aria-hidden="true">
          <source src={VIDEO_WEBM} type="video/webm" />
          <source src={VIDEO_MP4}  type="video/mp4"  />
        </video>
      </div>

      {/* ── Sharp video layer ── */}
      <div className="mw-video-wrap mw-video-wrap--clear">
        <video ref={clearVideoRef} className="mw-video"
          autoPlay muted loop playsInline preload="auto" aria-hidden="true">
          <source src={VIDEO_WEBM} type="video/webm" />
          <source src={VIDEO_MP4}  type="video/mp4"  />
        </video>
      </div>

      <div className="mw-bg-overlay" aria-hidden="true" />
      <div className="mw-grain"      aria-hidden="true" />

      {/* ══════════════════════════════════════════
          LANGUAGE SELECTOR SCREEN
      ══════════════════════════════════════════ */}
      {screen === 'lang' && (
        <main className="mw-stage">
          <div className={[
            'mw-lang-card',
            langReady   ? 'mw-lang-card--visible' : '',
            langLeaving ? 'mw-lang-card--leaving'  : '',
          ].filter(Boolean).join(' ')}>

            {/* Logo */}
            <div className="mw-wordmark mb-4">
              <img src="/images/logohome.png" alt="Mwuïlu" />
            </div>

            {/* Prompt */}
            <p className="mw-lang-prompt">Select your language</p>

            {/* Language options */}
            <div className="mw-lang-options" role="list">
              {LANGS.map((l, i) => (
                <button
                  key={l.code}
                  className="mw-lang-btn"
                  style={{ animationDelay: langReady ? `${i * 0.11 + 0.18}s` : '0s' }}
                  onClick={() => handleSelectLang(l)}
                  aria-label={`Select ${l.label}`}
                  role="listitem"
                >
                  <span className="mw-lang-btn__short">{l.short}</span>
                  <span className="mw-lang-btn__divider" aria-hidden="true" />
                  <span className="mw-lang-btn__full">{l.label}</span>
                </button>
              ))}
            </div>
          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          LOGIN SCREEN
      ══════════════════════════════════════════ */}
      {screen === 'login' && (
        <main className="mw-stage">
          <div className={`mw-card${loginReady ? ' mw-card--visible' : ''}`}>

            <div className="mw-wordmark mb-4">
              <img src="/images/logohome.png" alt="Mwuïlu" />
            </div>

            {/* Language back-link */}
            <button
              className="mw-lang-back"
              onClick={() => {
                setLoginReady(false)
                setTimeout(() => {
                  setScreen('lang')
                  setLang(null)
                  setTimeout(() => setLangReady(true), 60)
                }, 400)
              }}
              aria-label="Change language"
            >
              <span className="mw-lang-back__flag">{lang?.short ?? 'ENG'}</span>
              <span className="mw-lang-back__chevron" aria-hidden="true">‹</span>
              <span>Change language</span>
            </button>

            <form onSubmit={handleLogin} noValidate aria-label="Member login" className="w-100">
              <div className="mw-field-group">
                <div className="mw-field">
                  <label className="mw-label" htmlFor="memberId">Member ID</label>
                  <input
                    id="memberId"
                    type="text"
                    className={`mw-input${error ? ' mw-input--error' : ''}`}
                    value={memberId}
                    onChange={e => { setMemberId(e.target.value); clearError() }}
                    autoComplete="off"
                    spellCheck={false}
                    aria-required="true"
                  />
                </div>
                <div className="mw-field">
                  <label className="mw-label" htmlFor="accessCode">Access Code</label>
                  <input
                    id="accessCode"
                    type="password"
                    className={`mw-input${error ? ' mw-input--error' : ''}`}
                    value={accessCode}
                    onChange={e => { setAccessCode(e.target.value); clearError() }}
                    autoComplete="current-password"
                    aria-required="true"
                  />
                </div>
              </div>

              {error && <p className="mw-error" role="alert">{error}</p>}

              <button type="submit" className="mw-btn" disabled={isRevealing}>
                {isRevealing ? (
                  <span className="mw-btn__loading" aria-label="Signing in">
                    <span /><span /><span />
                  </span>
                ) : 'Enter'}
              </button>
            </form>
          </div>
        </main>
      )}

      {/* ── Footer ── */}
      <footer className="mw-footer">
        <p>
          Mwuïlu serves a private circle of embassies, consulates general,
          diplomatic staff, international institutions, and selected private clients. <br/>
          Membership is by invitation only.
        </p>
      </footer>

    </div>
  )
}

export default Home